
// src/connects/kpi-chart-page.js

import { connect } from 'react-redux'
import ui from '../helpers/ui-state'
import { translate } from 'react-i18next'
import lifecycle from '../helpers/lifecycle'
import actions from '../actions'
import combineHOC from '../helpers/combine-hoc'
import { trackPage } from '../helpers/analytics'
import { emitAlertMessage } from '../actions/alert-message'
import moment from 'moment'
import map from 'lodash/map'
import filter from 'lodash/filter'
import find from 'lodash/find'
import reduce from 'lodash/reduce'

export function filterMonthlyProgress(progress) {
  return map(progress, (item)=> Object.assign({}, item, {
    actual_records: filter(item.actual_records, (record)=> {
      return moment(record.reported_at).endOf('month').isSame(record.reported_at, 'date')
    })
  }))
}

export function calcHighchartData(kpi, progress, t) {
  var totals = []
  var targets = []
  var series = map(progress, (item)=> ({
    user_id: item.user_id,
    name: item.name,
    type: 'column',
    data: map(item.actual_records, (record, i)=> {
      var actual = typeof record.total == 'undefined' ? record.actual : record.total
      totals[i] = totals[i] ? totals[i] + actual : actual
      targets[i] = targets[i] ? targets[i] + record.target : record.target
      return actual
    })
  }))
  series.push({
    name: t('label:達成率'),
    type: 'line',
    yAxis: 1,
    data: map(targets, (target, i)=>
      targets[i] > 0 && Math.floor(totals[i] * 1000 / targets[i]) / 10 || 0
    )
  })
  // yAxisで定義されているplotLinesの指定だけだとデータ範囲によっては達成ラインが見えない時があるため、seriesとplotLinesを併用している
  series.push({
    name: t('label:達成ライン'),
    type: 'line',
    yAxis: 1,
    color: '#ff0000',
    data: map(targets, (target)=>100),
    marker: { enabled: false },
    lineWidth: 1,
    dashStyle: 'ShortDash'
  })
  return {
    chart: {
      type: 'column',
      events: {
        load: function() {
          var chart = this;
          chart.series.forEach(function(item, i) {
            var legendItem = chart.legend.allItems[i].legendGroup.element;
            legendItem.addEventListener('click', function() {
              var tableRows = document.querySelectorAll('#tableContainer table tr')
              var targetRow = null
              tableRows.forEach(function(row) {
                if(row.getAttribute('user_id') == item.userOptions.user_id) {
                  targetRow = row;
                }
              })
              if (targetRow) {
                targetRow.style.backgroundColor = item.visible ? 'white' : 'lightgray'
              }
            })
          })
        }
      }
    },
    title: { text: '' },
    credits: { enabled: false },
    exporting: { enabled: false },
    xAxis: {
      categories: map(progress[0].actual_records, (item)=> item.reported_at),
    },
    yAxis: [
      { title: { text: '' }, labels: { format: '{value}' + kpi.unit },
    },
      { title: { text: '' }, labels: { format: '{value}%' }, opposite: true, min: 0, tickAmount: 1,
        plotLines: [{ // 100%ラベルの表示
          width: 0,
          value: 100,
          label: {
            text: '100%',
            align: 'right',
            x: 45,
            y: 5,
            style: {
              fontWeight: 'bold'
            }
          },
        }] },
    ],
    plotOptions: { column: { stacking: 'normal' } },
    series
  }
}

export function calcComparisonHighchartData(comparison_kpis, t, ui) {
  if (!Array.isArray(comparison_kpis) || comparison_kpis.length == 0) {
    return {};
  }

  // Helper function to calculate monthly totals for each KPI
  const calcMonthlyTotals = (comparisonKpis) => {
    return comparisonKpis.map((kpi) => {
      const monthlyTotals = Array(12).fill(0);

      kpi.assignments.forEach((assignment) => {
        assignment.progress.monthly_totals.forEach((monthlyData, index) => {
          monthlyTotals[index] += monthlyData.actual_total || 0; // Safeguard for missing data
        });
      });

      const kpiYear = kpi.assignments && kpi.assignments[0] && kpi.assignments[0].progress && kpi.assignments[0].progress.monthly_totals && kpi.assignments[0].progress.monthly_totals[0] ? kpi.assignments[0].progress.monthly_totals[0].month : '';
      const formattedYear = kpiYear ? ` [${moment(kpiYear).format('YYYY')}]` : '';

      return {
        kpi_id: kpi.kpi_id,
        name: `${kpi.name}${formattedYear}`,
        type: 'column',
        data: monthlyTotals,
      };
    });
  };

  // Generate series data
  const series = calcMonthlyTotals(comparison_kpis);

  return {
    chart: {
      type: 'column',
      events: {
        load: function () {
          // Optional: Add custom chart load logic here
        },
      },
    },
    title: { text: '' },
    credits: { enabled: false },
    exporting: { enabled: false },
    xAxis: {
      categories: ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'],
    },
    yAxis: [
      {
        title: { text: '' },
        labels: {
          format: `{value}${comparison_kpis[0] && comparison_kpis[0].unit ? comparison_kpis[0].unit : ''}`, // Safeguard for missing unit
        },
      },
    ],
    plotOptions: {
      column: {
        stacking: ui.isStacked ? 'normal' : null,
        grouping: !ui.isStacked,
      },
    },
    series,
  };
}

export function calcColumnLineHighchartData(selectedKPIs, kpis, t, period = 'daily', ui) {
  if (!kpis || !Array.isArray(kpis) || kpis.length == 0) {
    return {};
  }

  const kpiActuals = {}; // Store KPI actuals by KPI ID
  const kpiTotalValues = {}; // Store KPI values for all KPIs
  const kpiTargets = {}; // Store KPI targets by KPI ID
  const kpiCumulativeActuals = {}; // Store KPI cumulative actuals by KPI ID
  const format = period == 'daily' ? 'YYYY-MM-DD' : period == 'monthly' ? 'YYYY-MM' : 'YYYY';

  // Helper function to aggregate data
  function aggregateData(records) {
    const groupedData = {};
    for (const record of records) {
      const dateKey = moment(record.reported_at).format(format);
      if (!groupedData[dateKey]) {
        groupedData[dateKey] = { totalActual: 0, totalTarget: 0 };
      }
      groupedData[dateKey].totalActual += record.actual || 0;
      groupedData[dateKey].totalTarget += record.target || 0;
    }
    return Object.entries(groupedData).map(([key, values]) => ({
      key,
      totalActual: values.totalActual,
      totalTarget: values.totalTarget
    }));
  }

  let xAxisCategories;  // Generate x-axis categories
  
  let record_length = kpis[0].assignments[0] ? kpis[0].assignments[0].progress.actual_records.length : 0;
  let kpiTotalTargetsArray = Array(record_length).fill(0); // total targets for all KPIs

  // Generate series data
  let series = selectedKPIs.flatMap((selected_kpi) => {
    const kpi = kpis.find((k) => k.kpi_id == selected_kpi.kpi_id);
    if (!kpi) return null;

    if (kpi.accumulated_total) {
      kpiTotalTargetsArray = kpiTotalTargetsArray.map(item => item + kpi.target);
    }
  
    if (!kpiActuals[kpi.kpi_id]) {
      kpiActuals[kpi.kpi_id] = [];
    }
    
    if (!kpiTotalValues[kpi.kpi_id]) {
      kpiTotalValues[kpi.kpi_id] = [];
    }

    if (!kpiTargets[kpi.kpi_id]) {
      kpiTargets[kpi.kpi_id] = [];
    }

    if (!kpiCumulativeActuals[kpi.kpi_id]) {
      kpiCumulativeActuals[kpi.kpi_id] = [];
    }

    if (ui.isIndividual) {
      // Create individual user series
      return kpi.assignments.map((assignment) => {
        const aggregatedData = aggregateData(assignment.progress.actual_records);

        xAxisCategories = aggregatedData.map(item => item.key)

        return {
          kpi_id: kpi.kpi_id,
          name: `${assignment.name} (${kpi.name})`,
          type: ui.chartType,
          data: aggregatedData.map((data, i) => {
            const actual = data.totalActual;
            const target = data.totalTarget;

            kpiActuals[kpi.kpi_id][i] = actual;
            kpiTargets[kpi.kpi_id][i] = target;

            return actual;
          })
        };
      });
    } else {
      // Aggregate data for all users under this KPI
      const aggregatedAssignmentData = kpi.assignments.map((assignment) =>
        aggregateData(assignment.progress.actual_records)
      );

      const aggregatedData = aggregatedAssignmentData.reduce((resultData, assignmentData) => {
        for (const data of assignmentData) {
          if (!resultData[data.key]) {
            resultData[data.key] = { totalActual: 0, totalTarget: 0 };
          }
          resultData[data.key].totalActual += data.totalActual;
          resultData[data.key].totalTarget += data.totalTarget;
        }
        return resultData;
      }, {});
      
      xAxisCategories = Object.keys(aggregatedData);

      return {
        kpi_id: kpi.kpi_id,
        name: kpi.name,
        type: ui.chartType,
        data: Object.values(aggregatedData).map((data, i) => {
          
          const actual = data.totalActual;
          const target = data.totalTarget;

          kpiActuals[kpi.kpi_id][i] = actual;

          const daily_target = record_length > 0 ? target / record_length : 0;
          kpiTargets[kpi.kpi_id][i] = kpi.accumulated_total ? daily_target * (i + 1) : target;

          kpiCumulativeActuals[kpi.kpi_id][i] = (kpiCumulativeActuals[kpi.kpi_id][i - 1] || 0) + actual;
          kpiTotalValues[kpi.kpi_id][i] = kpi.accumulated_total ? kpiCumulativeActuals[kpi.kpi_id][i] : actual;
          if (!kpi.accumulated_total) kpiTotalTargetsArray[i] = kpiTotalTargetsArray[i] + (i + 1) * kpi.target;
          return ui.isCumulative ? kpiCumulativeActuals[kpi.kpi_id][i] : actual;
        })
      };
    }
  }).filter(Boolean);

  if (ui.isConversion && !ui.isIndividual) {
    // Add conversion rate series
    for (let i = 0; i < selectedKPIs.length - 1; i++) {
      const kpiAData = kpiActuals[selectedKPIs[i].kpi_id];
      const kpiBData = kpiActuals[selectedKPIs[i + 1].kpi_id];

      if (kpiAData && kpiBData) {
        const conversionRates = kpiBData.map((value, index) =>
          kpiAData[index] > 0 ? Math.floor((value * 1000) / kpiAData[index]) / 10 : 0
        );

        series.push({
          name: `${t('label:歩留まり')}: ${selectedKPIs[i + 1].label} / ${selectedKPIs[i].label}`,
          type: 'line',
          yAxis: 1,
          dashStyle: 'ShortDash',
          data: conversionRates,
          tooltip: { valueSuffix: '%' }
        });
      }
    }
  }

  if (ui.isCumulative) {
    // calculate cumulative totals for all KPIs
    const kpiCumulativeTotals = Object.values(kpiCumulativeActuals).reduce((acc, arr) => {
      arr.forEach((value, index) => {
        acc[index] = (acc[index] || 0) + value;
      });
      return acc;
    }, []);

    // add cumulative yellow line
    series.push({
      name: t('label:達成率'),
      type: "line",
      yAxis: 1,
      color: '#ff9205',
      data: map(kpiCumulativeTotals, (total, i)=> 
        kpiTotalTargetsArray[i] > 0 && Math.floor(kpiCumulativeTotals[i] * 1000 / kpiTotalTargetsArray[i]) / 10 || 0
      )
    });

    // add 100% cumulative red line
    series.push({
      name: t('label:達成ライン'),
      type: 'line',
      yAxis: 1,
      color: '#ff0000',
      data: map(kpiCumulativeTotals, (total)=>100),
      marker: { enabled: false },
      lineWidth: 1,
      dashStyle: 'ShortDash'
    })
  }

  if (!ui.isCumulative && (!ui.isIndividual && !ui.isComparison)) {
    // calculate total actuals for all KPIs
    const kpiTotals = Object.values(kpiActuals).reduce((acc, arr) => {
      arr.forEach((value, index) => {
        acc[index] = (acc[index] || 0) + value;
      });
      return acc;
    }, []);

    // calculate total actuals for all KPIs
    const kpiAllValues = Object.values(kpiTotalValues).reduce((acc, arr) => {
      arr.forEach((value, index) => {
        acc[index] = (acc[index] || 0) + value;
      });
      return acc;
    }, []);

    // calculate total daily targets for all KPIs
    const kpiTotalTargets = Object.values(kpiTargets).reduce((acc, arr) => {
      arr.forEach((value, index) => {
        acc[index] = (acc[index] || 0) + value;
      });
      return acc;
    }, []);
    
    // add achievement rate yellow line
    series.push({
      name: t('label:達成率'),
      type: "line",
      yAxis: 1,
      color: '#ff9205',
      data: map(kpiTotals, (total, i)=> 
        kpiTotalTargets[i] > 0 && Math.floor(kpiAllValues[i] * 1000 / kpiTotalTargets[i]) / 10 || 0
      )
    });

    // add 100% cumulative red line
    series.push({
      name: t('label:達成ライン'),
      type: 'line',
      yAxis: 1,
      color: '#ff0000',
      data: map(kpiTotals, (total)=>100),
      marker: { enabled: false },
      lineWidth: 1,
      dashStyle: 'ShortDash'
    })
  }

  // Final chart configuration
  return {
    chart: {
      type: ui.chartType,
      height: 500,
      events: {
        load: function () {
          const chart = this;
        }
      }
    },
    title: { text: '' },
    credits: { enabled: false },
    exporting: { enabled: false },
    xAxis: { categories: xAxisCategories },
    yAxis: [
      { title: { text: '' }, labels: { format: `{value}${kpis[0].unit}` } },
      { title: { text: '' }, labels: { format: '{value}%' }, opposite: true, min: 0, tickAmount: 1,
        plotLines: [{
          width: 0,
          value: 100,
          label: {
            text: '100%',
            align: 'right',
            x: 45,
            y: 5,
            style: { fontWeight: 'bold' }
          }
        }]
      }
    ],
    legend: {
      enabled: true,
      align: 'center',
      layout: 'horizontal',
      verticalAlign: 'bottom',
      itemStyle: {
        fontWeight: 'bold',
        color: '#333',
      },
      maxHeight: 100,
    },
    plotOptions: {
      column: {
        stacking: ui.isStacked ? 'normal' : null,
        grouping: !ui.isStacked,
      }
    },
    series
  };
}

export function calcPieHighchartData(selectedKPIs, kpis, t, ui) {
  if (!kpis || !Array.isArray(kpis) || kpis.length == 0 || !Array.isArray(selectedKPIs)) {
    return {};
  }

  // Helper function to aggregate data for pie slices
  function aggregateData(records) {
    return records.reduce((total, record) => total + (record.actual || 0), 0);
  }

  // Generate data for the pie chart
  const pieData = selectedKPIs.flatMap((selected_kpi) => {
    const kpi = kpis.find((k) => k.kpi_id === selected_kpi.kpi_id);
    if (!kpi) return null;

    if (ui.isIndividual) {
      // Individual user slices
      return kpi.assignments.map((assignment) => {
        const totalActual = aggregateData(assignment.progress.actual_records);
        return {
          name: `${assignment.name} (${kpi.name})`,
          y: totalActual,
        };
      });
    } else {
      // Aggregated KPI slice
      const totalActual = kpi.assignments.reduce(
        (sum, assignment) => sum + aggregateData(assignment.progress.actual_records),
        0
      );
      return {
        name: kpi.name,
        y: totalActual,
      };
    }
  }).filter(Boolean);

  // Final chart configuration for a pie chart
  return {
    chart: {
      type: 'pie',
      height: 500,
    },
    title: { text: '' },
    credits: { enabled: false },
    exporting: { enabled: false },
    tooltip: {
      pointFormat: '<b>{point.percentage:.1f}%</b> ({point.y})',
    },
    legend: {
      enabled: true,
      align: 'center',
      layout: 'horizontal',
      verticalAlign: 'bottom',
      itemStyle: {
        fontWeight: 'bold',
        color: '#333',
      },
      maxHeight: 100,
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: 'pointer',
        dataLabels: {
          enabled: false,
        },
        showInLegend: true,
      },
    },
    series: [
      {
        name: t('label:合計'), // Localized label
        colorByPoint: true,
        data: pieData,
      },
    ],
  };
}

export const mapStateToProps = (state, ownProps)=> {
  const kpi = state.kpiChart.current && state.kpiChart.current.kpi_id == ownProps.params.kpi_id && state.kpiChart.current || undefined

  return {
    isFetching: state.kpiChart.isFetching,
    isUpdating: state.kpiChart.isUpdating,
    isUpdateFailed: state.kpiChart.isUpdateFailed,
    kpis: state.kpi.chart_items,
    group_kpis: state.kpi.group_items,
    comparison_kpis: state.kpi.comparison_items,
    kpi: kpi,
    group: state.groups.items && find(state.groups.items, (item)=> item.group_id == ownProps.params.group_id) || undefined,
    user: state.user.current,
  }
}

export const mergeProps = (stateProps, dispatchProps, ownProps) => {
  return {
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    group: stateProps.group || ownProps.group
  }
}

export const mapDispatchToProps = (dispatch, ownProps)=> {
  return {
    willMount: (props)=> dispatch(actions.fetchKpiChart(props.params.kpi_id, props.params.group_id)),
    willUnmount: ()=> {
      dispatch(actions.clearKpiChart())
    },

    willUpdate: (props, nextProps)=> {
      if (!props.kpi && nextProps.kpi || props.kpi && nextProps.kpi && props.kpi.kpi_id != nextProps.kpi.kpi_id) {
        dispatch(actions.fetchChartKpis(nextProps.params.group_id, moment(nextProps.kpi.start_date).format('YYYY-MM-DD'), moment(nextProps.kpi.end_date).format('YYYY-MM-DD'), [{ kpi_id: nextProps.kpi.kpi_id }]))
        // Get group kpis list
        dispatch(actions.fetchGroupKpis(nextProps.params.group_id))
        // Initialize comparison kpi list
        dispatch(actions.fetchComparisonKpis([{ kpi_id: nextProps.kpi.kpi_id, year: moment(nextProps.kpi.end_date).format('YYYY') }], nextProps.params.group_id))
      }

      if (props.ui.kpiStartDate && props.ui.kpiStartDate != nextProps.ui.kpiStartDate || props.ui.kpiEndDate && props.ui.kpiEndDate != nextProps.ui.kpiEndDate) {
        dispatch(actions.fetchChartKpis(nextProps.params.group_id, moment(nextProps.ui.kpiStartDate).format('YYYY-MM-DD'), moment(nextProps.ui.kpiEndDate).format('YYYY-MM-DD'), nextProps.ui.selectedKPIs))
      }

      if (props.params.kpi_id != nextProps.params.kpi_id)
        dispatch(actions.fetchKpiChart(nextProps.params.kpi_id, nextProps.params.group_id))
    },

    openKPIChartPage: (props, kpi_id)=> dispatch(actions.openRightSidePage('kpi_chart', { kpi_id, group_id: props.params.group_id })),
    openGroupKPIPanel: ()=> dispatch(actions.openGroupSettingsPage('kpi')),

    fetchChartKpis: (selected_kpis, props) => {
      dispatch(actions.fetchChartKpis(props.params.group_id, moment(props.ui.kpiStartDate).format('YYYY-MM-DD'), moment(props.ui.kpiEndDate).format('YYYY-MM-DD'), selected_kpis))
    },

    fetchComparisonKpis: (selected_kpis, props) => {
      if (selected_kpis.length == 0) {
        selected_kpis = [{ kpi_id: props.kpi.kpi_id, year: moment(props.kpi.end_date).format('YYYY') }];
      } 

      dispatch(actions.fetchComparisonKpis(selected_kpis, props.params.group_id))
    },
  }
}

export default combineHOC(
  connect(mapStateToProps, mapDispatchToProps, mergeProps),
  ui({ state: {
    index: 0,
    clientWidth: 1000,
    selected_user_id: undefined,
    selected_user_name: undefined,
    showKPIDataSetting: true,
    selectedKPIs: [],
    kpiStartDate: null,
    kpiEndDate: null,
    chartType: 'column',
    isStacked: false,
    isIndividual: false,
    isConversion: false,
    isComparison: false,
    isCumulative: false,
    aggregationType: 'cumulative',
    tableSelectedDate: null,
    tableStartDate: null,
    tableEndDate: null,
  }}),
  lifecycle,
  translate(),
  trackPage('kpi_chart'),
)



// WEBPACK FOOTER //
// ./src/connects/kpi-chart-page.js